import { Box, Checkbox, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  FilterType,
  ProjectionKind,
  buildReconciliationInvoicesDataTableFilter,
  useReconciliationInvoicesDataTable,
} from 'generated/resources/datatable.graphql'
import { FinanceValueObjectsFinancialDocumentTypeEnum } from 'generated/resources/graphql'
import { sum } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ReconciliationItem } from 'modules/banking/hooks/useReconciliationForm'
import { invoiceType } from 'modules/banking/lib/invoicesHelpers'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

interface Props {
  onSelect: (value: ReconciliationItem) => void
  onRemove: (value: ReconciliationItem) => void
  onInvoiceDetailClick: (id: string, expensableId?: string) => void
  isSelected: (id: string) => boolean
  disabled: boolean
  currency?: string
}

export interface InvoicesDataTableFields {
  id: string
  description?: string
  totalAmountCents?: number | string | null
  currency?: string | null
  reconciliations: {
    __typename?: 'BankingReconciliation'
    id: string
    reconcilableReconciledAmountCents?: number | string | null
  }[]
}

const getPendingAmount = (invoice: InvoicesDataTableFields) => {
  const totalReconciledAmountCents =
    sum(invoice.reconciliations.map((r) => Number(r.reconcilableReconciledAmountCents ?? 0))) || 0
  return Math.abs(Number(invoice.totalAmountCents)) - totalReconciledAmountCents
}
const EmptyBlock = ({ currency }: { currency?: string | null }) => (
  <Box
    flexDirection="column"
    gap="s8"
    width="s400"
    paddingX="s0"
    alignItems="center"
    textAlign="center"
    style={{ marginTop: '-20px' }}
  >
    <Icon.Large icon="file" color="grey600" />
    <Box flexDirection="column" gap="s4" alignItems="center">
      <Text.Header2>{i18n.t('banking.reconciliations.invoices_empty_state.title')}</Text.Header2>
      <Text.Normal color="grey800">
        {currency
          ? i18n.t('banking.reconciliations.invoices_empty_state.description_currency', {
              currency: currency.toUpperCase(),
            })
          : i18n.t('banking.reconciliations.invoices_empty_state.description')}
      </Text.Normal>
    </Box>
  </Box>
)

export const ReconciliationsInvoicesDataTable = ({
  onSelect,
  onRemove,
  isSelected,
  disabled,
  onInvoiceDetailClick,
  currency,
}: Props) => {
  const { company, access } = useCurrentSession()
  const locale = access.locale
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const onRowClick = (
    id: string,
    documentType: FinanceValueObjectsFinancialDocumentTypeEnum,
    expensableId: string | undefined
  ) => {
    onInvoiceDetailClick(id, documentType == 'receipt' ? (expensableId ?? undefined) : undefined)
  }

  const source = useReconciliationInvoicesDataTable(
    [
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 0,
        render: (invoice) => {
          const pendingAmountCents = getPendingAmount(invoice)
          return (
            <Box flexDirection="row">
              <Checkbox
                disabled={disabled}
                checked={isSelected(invoice.id)}
                onChange={(event) => {
                  const reconciliation: ReconciliationItem = {
                    id: invoice.id,
                    description: invoice.documentNumber || '-',
                    secondaryDescription: contactFullName(invoice.vendor),
                    amountCentsToReconcile: pendingAmountCents,
                    currency: invoice.currency || '',
                    date: invoice.documentDate || '',
                    locale: locale,
                    type: invoiceType(
                      true, // We are assuming all invoices are expenses invoices and not income invoices
                      invoice.totalAmountCents ?? '0'
                    ),
                  }
                  if (event.target.checked) {
                    onSelect(reconciliation)
                  } else {
                    onRemove(reconciliation)
                  }
                }}
              />
            </Box>
          )
        },
      },
      {
        identifier: 'documentDate',
        title: i18n.t('finance.invoices.list.fields.date'),
        kind: ProjectionKind.Custom,
        sortBy: 'documentDate',
        width: 100,
        render: ({ documentDate, id, expensesFinancialDocumentAssociation, documentType }) => (
          <Box
            cursor="pointer"
            onClick={() =>
              onRowClick(
                id,
                documentType,
                expensesFinancialDocumentAssociation?.expense?.expensable?.id
              )
            }
          >
            <Text.Normal size="200">
              {documentDate ? dateBackendDateTimeToUI(String(documentDate)) : '-'}
            </Text.Normal>
          </Box>
        ),
        template: '{{ documentDate }}',
      },
      {
        identifier: 'vendor',
        title: i18n.t('finance.invoices.list.fields.vendor'),
        kind: ProjectionKind.Custom,
        render: ({
          vendor,
          documentNumber,
          id,
          expensesFinancialDocumentAssociation,
          documentType,
        }) => (
          <Box
            style={{ height: '56px' }}
            justifyContent="center"
            gap="s4"
            cursor="pointer"
            onClick={() =>
              onRowClick(
                id,
                documentType,
                expensesFinancialDocumentAssociation?.expense?.expensable?.id
              )
            }
          >
            <Text.Normal ellipsis size="200">
              {documentNumber}
            </Text.Normal>
            <Text.Normal ellipsis size="100">
              {contactFullName(vendor)}
            </Text.Normal>
          </Box>
        ),
        template: '{{ vendor.legalName }}',
      },
      {
        identifier: 'pendingAmount',
        title: i18n.t('finance.invoices.list.fields.pendingAmount'),
        width: 120,
        kind: ProjectionKind.Custom,
        render: (invoice) => {
          const pendingAmountCents = getPendingAmount(invoice)
          return (
            <Box
              cursor="pointer"
              onClick={() =>
                onRowClick(
                  invoice.id,
                  invoice.documentType,
                  invoice.expensesFinancialDocumentAssociation?.expense?.expensable?.id
                )
              }
            >
              <Text.Normal ellipsis weight="medium" size="200" color="grey1000">
                {formattedAmount({
                  amountCents: Number(pendingAmountCents),
                  currency: invoice.currency ?? 'EUR',
                  locale,
                })}
              </Text.Normal>
            </Box>
          )
        },

        template: '{{ totalAmountCents }}',
      },
      {
        identifier: 'amount',
        title: i18n.t('finance.invoices.list.fields.amount'),
        width: 130,
        sortBy: 'totalAmountCents',
        kind: ProjectionKind.Custom,
        render: ({
          totalAmountCents,
          currency,
          id,
          expensesFinancialDocumentAssociation,
          documentType,
        }) => (
          <Box
            cursor="pointer"
            onClick={() =>
              onRowClick(
                id,
                documentType,
                expensesFinancialDocumentAssociation?.expense?.expensable?.id
              )
            }
          >
            <Text.Normal ellipsis weight="medium" size="200" color="grey1000">
              {totalAmountCents
                ? formattedAmount({
                    amountCents: Number(totalAmountCents),
                    currency: currency ?? 'EUR',
                    locale,
                  })
                : '-'}
            </Text.Normal>
          </Box>
        ),
        template: '{{ totalAmountCents }}',
      },
    ],
    {
      variables: {
        companyId: company.id,
        search: debouncedSearch,
        currency,
        reconcilable: true,
      },
    }
  )

  const dateFilter = buildReconciliationInvoicesDataTableFilter(source, {
    identifier: 'date',
    title: i18n.t('finance.invoices.list.fields.date'),
    path: 'documentDate',
    default: false,
    type: FilterType.Date,
    categories: [
      {
        kind: 'DatePicker',
        operation: 'between',
      },
    ],
  })

  return (
    <Box width="full">
      <DataTable
        density="compact"
        disableEditColumns
        persistFilters={false}
        pagination={{
          itemsPerPage: 5,
          kind: 'offset',
        }}
        source={source}
        sort={{ path: 'documentNumber', direction: 'asc' }}
        filters={[dateFilter]}
        disableExport
        search={{ placeholder: i18n.t('common.search'), value: search, onChange: onSearch }}
        emptyState={<EmptyBlock currency={currency} />}
      />
    </Box>
  )
}
